/**
 * Canonical `deals.deal_status` vocabulary — single source of truth.
 *
 * The land team rationalised the legacy 21-value MSSQL sprawl (Identified /
 * Contacted - Phone / Reviewing / Title Failed / Minerals Sold / …) down to
 * the 10 values below. de-hub seeds them (migration 039, and the managed
 * `pick_lists` row set that backs Admin → Pick Lists) and every deal row in
 * the database now carries one of them — the legacy strings are flagged
 * inactive and no longer appear in any dropdown.
 *
 * Everything in this app that buckets deals by status must read its scopes
 * from here. Before this module the KPI strip, dealMetrics.js and the Kanban
 * each kept their own copy of the legacy 21-value sets, so every status test
 * silently missed: "Total Open Deals" counted dead deals as open because
 * `Set{"Rejected","Invalid","Do Not Call","Minerals Sold"}` never matches
 * "REJECTED BY SELLER" / "DECLINED BY WARWICK" / "DO NOT CALL".
 *
 * Stored values are UPPERCASE. The helpers normalise (trim + upper-case)
 * before comparing, so a historical row still carrying a legacy mixed-case
 * value classifies the same way; the legacy dead-end statuses that have no
 * canonical equivalent are listed in LEGACY_DEAD_STATUSES so they can't
 * quietly count as open either.
 */

// Workflow order — matches `deal_status.ord` / `pick_lists.sort_order`.
export const DEAL_STATUSES = [
  "NOT CONTACTED",
  "CONTACTED",
  "DO NOT CALL",
  "NEGOTIATING",
  "COMMITTED NON-BINDING",
  "COMMITTED BINDING",
  "DECLINED BY WARWICK",
  "REJECTED BY SELLER",
  "WAITING ON WARWICK",
  "CLOSED",
];

export const CLOSED_STATUS = "CLOSED";

// Dead — the deal will not advance and no further work is expected.
export const DEAD_STATUSES = new Set([
  "DO NOT CALL",
  "DECLINED BY WARWICK",
  "REJECTED BY SELLER",
]);

// Legacy dead-end statuses with no canonical equivalent. No live row carries
// one today, but historical / re-imported rows might, and a dead deal counted
// as open is the exact bug this module exists to prevent.
export const LEGACY_DEAD_STATUSES = new Set([
  "REJECTED",
  "INVALID",
  "MINERALS SOLD",
  "LEASE TO COMPETITOR",
  "LESSOR DECEASED",
  "TOP LEASED",
  "WEG INTERNAL REJECT",
]);

// Terminal — dead plus CLOSED (won a deal is still an end state).
export const TERMINAL_STATUSES = new Set([...DEAD_STATUSES, CLOSED_STATUS]);

// Financial-aggregate scopes (lease-analyst definitions, re-mapped onto the
// canonical vocabulary):
//   headline Total Bonus = COMMITTED BINDING + CLOSED
//   pipeline Bonus       = NEGOTIATING + COMMITTED NON-BINDING +
//                          WAITING ON WARWICK
export const COMMITTED_OR_CLOSED_STATUSES = new Set([
  "COMMITTED BINDING",
  "CLOSED",
]);

export const PIPELINE_STATUSES = new Set([
  "NEGOTIATING",
  "COMMITTED NON-BINDING",
  "WAITING ON WARWICK",
]);

// Per-status stale thresholds (days since the last status change), carried
// over from the landman's review of the legacy vocabulary:
//   Identified 14 -> NOT CONTACTED, Contacted-* 7 -> CONTACTED,
//   Reviewing 7 -> WAITING ON WARWICK, Negotiating 10, Committed 14 / 21.
// Terminal statuses are absent on purpose — they are never "open", so they
// are never evaluated for staleness.
export const STALE_THRESHOLDS_DAYS = {
  "NOT CONTACTED": 14,
  CONTACTED: 7,
  NEGOTIATING: 10,
  "COMMITTED NON-BINDING": 14,
  "COMMITTED BINDING": 21,
  "WAITING ON WARWICK": 7,
};

// Fallback threshold for a status with no explicit entry.
export const DEFAULT_STALE_DAYS = 30;

/** Trim + upper-case a raw status value. Returns "" for null/undefined. */
export const normalizeStatus = (status) =>
  status == null ? "" : String(status).trim().toUpperCase();

/**
 * Read a deal row's status, tolerating both key conventions: the Postgres
 * endpoints serve `deal_status`, while pages fed by `useBrokerDeals` also
 * carry the mirrored UPPER_SNAKE `DEAL_STATUS`.
 */
export const dealStatusOf = (row) =>
  normalizeStatus(row?.deal_status ?? row?.DEAL_STATUS);

export const isDeadStatus = (status) => {
  const s = normalizeStatus(status);
  return DEAD_STATUSES.has(s) || LEGACY_DEAD_STATUSES.has(s);
};

export const isClosedStatus = (status) =>
  normalizeStatus(status) === CLOSED_STATUS;

export const isTerminalStatus = (status) =>
  isDeadStatus(status) || isClosedStatus(status);

/**
 * "Open" = still live work: not dead and not closed. An unknown or missing
 * status counts as open — an un-triaged deal is work, not an archive.
 */
export const isOpenStatus = (status) => !isTerminalStatus(status);

/** Row-level convenience wrappers. */
export const isDeadDeal = (row) => isDeadStatus(dealStatusOf(row));
export const isOpenDeal = (row) => isOpenStatus(dealStatusOf(row));
export const staleThresholdFor = (status) =>
  STALE_THRESHOLDS_DAYS[normalizeStatus(status)] ?? DEFAULT_STALE_DAYS;
